import { GapDraftReviewStatus, type GapDraftView } from '@momentum/types';

import { DEFAULT_READER_VIEW, type ReaderView } from './reader-view';

/**
 * Which drafts the queue shows, in what order, and how they group.
 *
 * Kept out of the components because the same list drives three things that must
 * agree: the rows, the J/K keyboard walk, and where auto-advance lands. A second
 * implementation of "the visible drafts" is a bug waiting to happen.
 *
 * Everything here works over ONE run's drafts. There is no cross-run list to
 * filter or order: the layout fetches the selected run alone, so which run is in
 * play is settled before any of this is asked.
 */

/** The queue's verdict filter. `todo` is the default — it is the working set. */
export type QueueFilter = 'todo' | 'keep' | 'drop' | 'all';

export const QUEUE_FILTERS: QueueFilter[] = ['todo', 'keep', 'drop', 'all'];

export function isQueueFilter(value: unknown): value is QueueFilter {
  return QUEUE_FILTERS.includes(value as QueueFilter);
}

/**
 * `APPROVED` is "kept" and `REJECTED` is "dropped" throughout this surface.
 *
 * The vocabulary is deliberate: a kept draft is not published — promotion into
 * the `gaps` collection is a separate, currently blocked step — so calling the
 * verdict "approved" on screen would promise something the button does not do.
 */
export const KEEP_STATUS = GapDraftReviewStatus.APPROVED;
export const DROP_STATUS = GapDraftReviewStatus.REJECTED;

const STATUS_BY_FILTER: Record<
  Exclude<QueueFilter, 'all'>,
  GapDraftReviewStatus
> = {
  todo: GapDraftReviewStatus.PENDING,
  keep: KEEP_STATUS,
  drop: DROP_STATUS,
};

/**
 * The queue's opening position, as search params — everything except which run.
 *
 * Exported because the topbar's "Review queue" link has to supply them, and this
 * is the single place the defaults are written. The run is deliberately not here:
 * it is resolved by the layout, which falls back to the most recent run when the
 * URL names none, so a link that omits it opens on the newest work rather than on
 * a hardcoded guess.
 */
export const DEFAULT_QUEUE_SEARCH: {
  filter: QueueFilter;
  view: ReaderView;
} = {
  filter: 'todo',
  view: DEFAULT_READER_VIEW,
};

/**
 * The drafts the queue shows, ordered scope-major then by gap id.
 *
 * `sourceGapId` alone is enough within a run — the ids restart at `R1-` every
 * execution, which is exactly why this used to need an execution tiebreak and no
 * longer does.
 */
export function visibleDrafts(
  drafts: GapDraftView[],
  filter: QueueFilter
): GapDraftView[] {
  return drafts
    .filter(
      (draft) =>
        filter === 'all' || draft.review.status === STATUS_BY_FILTER[filter]
    )
    .sort(
      (left, right) =>
        left.scopeName.localeCompare(right.scopeName) ||
        left.sourceGapId.localeCompare(right.sourceGapId)
    );
}

export interface QueueStatusCounts {
  pending: number;
  kept: number;
  dropped: number;
}

export function countByStatus(drafts: GapDraftView[]): QueueStatusCounts {
  return {
    pending: drafts.filter(
      (draft) => draft.review.status === GapDraftReviewStatus.PENDING
    ).length,
    kept: drafts.filter((draft) => draft.review.status === KEEP_STATUS).length,
    dropped: drafts.filter((draft) => draft.review.status === DROP_STATUS)
      .length,
  };
}

/** One scope's run of rows, with the header count the group head shows. */
export interface QueueScopeGroup {
  scopeExternalId: string;
  scopeName: string;
  draftCount: number;
  drafts: GapDraftView[];
}

/**
 * Splits an already-ordered list into its scope runs.
 *
 * The count in a group head is over `runDrafts` — the whole selected run — NOT
 * over the ordered list: "Sodra · 7 gaps" answers what the reviewer has picked to
 * work through, and would be useless if it changed every time they flipped a
 * verdict chip.
 */
export function groupByScope(
  orderedDrafts: GapDraftView[],
  runDrafts: GapDraftView[]
): QueueScopeGroup[] {
  const groups: QueueScopeGroup[] = [];

  for (const draft of orderedDrafts) {
    const openGroup = groups.at(-1);
    if (openGroup?.scopeExternalId === draft.scopeExternalId) {
      openGroup.drafts.push(draft);
      continue;
    }
    groups.push({
      scopeExternalId: draft.scopeExternalId,
      scopeName: draft.scopeName,
      draftCount: runDrafts.filter(
        (candidate) => candidate.scopeExternalId === draft.scopeExternalId
      ).length,
      drafts: [draft],
    });
  }

  return groups;
}

/**
 * The next draft still awaiting a verdict, wrapping past the end of the list.
 *
 * Wrapping matters on the `todo` filter: the draft just decided leaves the list,
 * so "the next one" is frequently at the position the current draft occupied,
 * and on the last row it is back at the top.
 */
export function nextUnreviewedAfter(
  orderedDrafts: GapDraftView[],
  draftId: string
): GapDraftView | undefined {
  const index = orderedDrafts.findIndex((draft) => draft._id === draftId);
  const searchOrder = [
    ...orderedDrafts.slice(index + 1),
    ...orderedDrafts.slice(0, Math.max(index, 0)),
  ];
  return searchOrder.find(
    (draft) => draft.review.status === GapDraftReviewStatus.PENDING
  );
}
